// Light or dark: as the device is set (the default), or chosen with the moon button in the sidebar. The CRM's dark
// colours sit in "(prefers-color-scheme: dark)" blocks; a choice rewrites those conditions to always / never, so every
// dark rule (also ones added later) follows it without a second copy of the colours.
export type Theme = 'auto' | 'light' | 'dark';
const KEY = 'crm.theme';
const DARK = '(prefers-color-scheme: dark)';
const ALWAYS = '(min-width: 0px)';
const NEVER = '(min-width: 100000px)';
const original = new WeakMap<CSSMediaRule, string>();

export function savedTheme(): Theme {
  try { const v = localStorage.getItem(KEY); return v === 'light' || v === 'dark' ? v : 'auto'; } catch { return 'auto'; }
}

/** Whether the page shows dark now. */
export function isDark(theme: Theme = savedTheme()): boolean {
  return theme === 'dark' || (theme === 'auto' && !!window.matchMedia?.(DARK).matches);
}

function rewrite(rules: CSSRuleList, theme: Theme) {
  for (const r of Array.from(rules)) {
    if (r instanceof CSSMediaRule) {
      const was = original.get(r) ?? r.media.mediaText;
      if (!original.has(r) && was.includes('prefers-color-scheme')) original.set(r, was);
      if (original.has(r)) {
        const next = theme === 'auto' ? was : was.replace(/\(\s*prefers-color-scheme:\s*dark\s*\)/g, theme === 'dark' ? ALWAYS : NEVER);
        if (r.media.mediaText !== next) r.media.mediaText = next;
      }
      rewrite(r.cssRules, theme);
    } else if ('cssRules' in r && (r as CSSGroupingRule).cssRules) rewrite((r as CSSGroupingRule).cssRules, theme);
  }
}

export function applyTheme(theme: Theme = savedTheme()) {
  for (const sheet of Array.from(document.styleSheets)) {
    try { rewrite(sheet.cssRules, theme); } catch { /* a stylesheet from another site can't be read */ }
  }
  document.documentElement.dataset.theme = isDark(theme) ? 'dark' : 'light';
  document.documentElement.style.colorScheme = isDark(theme) ? 'dark' : 'light';
}

export function setTheme(theme: Theme) {
  try { if (theme === 'auto') localStorage.removeItem(KEY); else localStorage.setItem(KEY, theme); } catch { /* this visit only */ }
  applyTheme(theme);
}

/** Applies the saved choice now and to stylesheets added later (lazy pages, the icon styles). */
export function startTheme() {
  applyTheme();
  const head = new MutationObserver((changes) => {
    if (savedTheme() === 'auto') return;
    applyTheme();
    // A stylesheet link is read once it has loaded.
    for (const c of changes) c.addedNodes.forEach((n) => { if (n instanceof HTMLLinkElement) n.addEventListener('load', () => applyTheme(), { once: true }); });
  });
  head.observe(document.head, { childList: true });
  window.addEventListener('load', () => applyTheme(), { once: true });
  window.matchMedia?.(DARK).addEventListener?.('change', () => { if (savedTheme() === 'auto') applyTheme('auto'); });
}
